import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Input,
  Label,
  FormGroup,
  Form,
} from "reactstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../Request";
import { URL as ApiURL } from "../api";
import { isFintechUser, getFintechId } from "../util/common";

// Turn a snake_case / camelCase key into a readable column header.
const humanize = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const renderCell = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const toISO = (d) => d.toISOString().split("T")[0];

const money = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DEFAULT_ID_FILTERS = [
  { label: "Customer ID", param: "customerId" },
  { label: "Loan ID", param: "loanId" },
  { label: "Payment ID", param: "paymentId" },
  { label: "Txn ID", param: "tranId" },
];

/**
 * Generic backend-connected list page with optional date, ID-search and LSP filters.
 *
 * Props: title, endpoint, columns, params, dateFilter, dateParamNames, defaultRangeDays,
 *  - idFilter:  when true, render a "filter type" dropdown + search input
 *  - idFilters: [{label, param}] for the dropdown (defaults to customer/loan/payment/txn id)
 *  - lspFilter: when true, render an LSP dropdown (fetched from LSP_NAMES)
 *  - lspParam:  query-param name for the LSP id (default "fintechId")
 */
const EntityList = ({
  title,
  endpoint,
  columns,
  params = {},
  dateFilter = false,
  dateParamNames = { from: "fromDate", to: "toDate" },
  defaultRangeDays = 365,
  // When true, the date pickers start empty so the backend applies its own default (e.g. today's
  // dues) and an id search is not silently constrained to a date range.
  defaultEmptyDates = false,
  idFilter = false,
  idFilters = DEFAULT_ID_FILTERS,
  lspFilter = false,
  lspParam = "fintechId",
  // When true, render a FIP / PIF amortization-scheme dropdown; the chosen value is sent as `scheme`.
  schemeFilter = false,
  // Optional status dropdown: pass an array of { label, value }; the chosen value is sent as `statusParam`.
  statusFilter = null,
  statusParam = "status",
  viewLink = null,
  // Optional per-column overrides:
  //  - labels:     { colKey: "Header Text" }  — override the auto-humanized column header
  //  - formatters: { colKey: (value, row) => node } — custom cell rendering (e.g. code / label maps)
  labels = {},
  formatters = {},
  // When set, fetch { advanceCollection, overdueCollection, totalCollection, paymentCount } for the
  // current date / id filters and render a summary metric bar above the table.
  summaryEndpoint = null,
}) => {
  const today = new Date();
  const past = new Date();
  past.setDate(past.getDate() - defaultRangeDays);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fromDate, setFromDate] = useState(dateFilter && !defaultEmptyDates ? toISO(past) : "");
  const [toDate, setToDate] = useState(dateFilter && !defaultEmptyDates ? toISO(today) : "");
  const [filterType, setFilterType] = useState(idFilters[0]?.param || "");
  const [filterValue, setFilterValue] = useState("");
  const [lspList, setLspList] = useState([]);
  const [selectedLsp, setSelectedLsp] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedScheme, setSelectedScheme] = useState("fip");
  const [page, setPage] = useState(0);
  const [summary, setSummary] = useState(null);
  const PAGE_SIZE = 20;

  // A fintech (LSP) login is locked to its own fintechId: no LSP dropdown, and every request
  // carries that id so the list only ever shows that LSP's data.
  const fintechUser = isFintechUser();
  const showLspFilter = lspFilter && !fintechUser;

  // Fetch the advance/overdue collection summary for the current date + id filters.
  const fetchSummary = useCallback(
    async (override) => {
      if (!summaryEndpoint) return;
      try {
        const q = {};
        const from = override ? override.from : fromDate;
        const to = override ? override.to : toDate;
        if (from) q.fromDate = from;
        if (to) q.toDate = to;
        const type = override ? override.filterType : filterType;
        const val = override ? override.filterValue : filterValue;
        if (val && type === "customerId") q.customerId = val;
        if (val && type === "loanId") q.loanId = val;
        if (schemeFilter) q.scheme = override && override.scheme != null ? override.scheme : selectedScheme;
        const res = await axiosInstance.get(summaryEndpoint, { params: q });
        setSummary(res.data || null);
      } catch (e) {
        console.error("Failed to fetch collection summary:", e);
        setSummary(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summaryEndpoint]
  );

  // Load LSP names once when the LSP filter is enabled.
  useEffect(() => {
    if (!showLspFilter) return;
    axiosInstance
      .get(ApiURL.LSP_NAMES)
      .then((res) => {
        const d = res.data;
        setLspList(Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []);
      })
      .catch(() => setLspList([]));
  }, [showLspFilter]);

  const fetchData = useCallback(
    async (override) => {
      setLoading(true);
      setError("");
      try {
        const query = { page: override && override.page != null ? override.page : 0, size: PAGE_SIZE, ...params };
        if (dateFilter) {
          const from = override ? override.from : fromDate;
          const to = override ? override.to : toDate;
          if (from) query[dateParamNames.from] = from;
          if (to) query[dateParamNames.to] = to;
        }
        if (idFilter) {
          const type = override ? override.filterType : filterType;
          const val = override ? override.filterValue : filterValue;
          if (type && val) query[type] = val;
        }
        if (lspFilter) {
          if (fintechUser) {
            query[lspParam] = getFintechId();
          } else {
            const lsp = override ? override.lsp : selectedLsp;
            if (lsp) query[lspParam] = lsp;
          }
        }
        if (statusFilter) {
          const st = override ? override.status : selectedStatus;
          if (st) query[statusParam] = st;
        }
        if (schemeFilter) {
          query.scheme = override && override.scheme != null ? override.scheme : selectedScheme;
        }
        const res = await axiosInstance.get(endpoint, { params: query });
        const payload = res.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.content)
          ? payload.content
          : [];
        setRows(list);
      } catch (e) {
        console.error(`Failed to fetch ${title}:`, e);
        setError(`Could not load ${title.toLowerCase()} data.`);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, title]
  );

  useEffect(() => {
    fetchData();
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, fetchSummary]);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setPage(0);
    fetchData({ from: fromDate, to: toDate, filterType, filterValue, lsp: selectedLsp, status: selectedStatus, scheme: selectedScheme, page: 0 });
    fetchSummary({ from: fromDate, to: toDate, filterType, filterValue, scheme: selectedScheme });
  };

  // FIP / PIF scheme change — apply immediately with the current filters.
  const handleSchemeChange = (scheme) => {
    setSelectedScheme(scheme);
    setPage(0);
    fetchData({ from: fromDate, to: toDate, filterType, filterValue, lsp: selectedLsp, status: selectedStatus, scheme, page: 0 });
    fetchSummary({ from: fromDate, to: toDate, filterType, filterValue, scheme });
  };

  const handleReset = () => {
    const resetFrom = dateFilter && !defaultEmptyDates ? toISO(past) : "";
    const resetTo = dateFilter && !defaultEmptyDates ? toISO(today) : "";
    setFilterValue("");
    setSelectedLsp("");
    setSelectedStatus("");
    setFromDate(resetFrom);
    setToDate(resetTo);
    setPage(0);
    fetchData({ from: resetFrom, to: resetTo, filterType, filterValue: "", lsp: "", status: "", scheme: selectedScheme, page: 0 });
    fetchSummary({ from: resetFrom, to: resetTo, filterType, filterValue: "", scheme: selectedScheme });
  };

  const goToPage = (p) => {
    if (p < 0) return;
    setPage(p);
    fetchData({ from: fromDate, to: toDate, filterType, filterValue, lsp: selectedLsp, status: selectedStatus, scheme: selectedScheme, page: p });
  };

  const cols =
    columns && columns.length
      ? columns
      : rows.length
      ? Object.keys(rows[0]).slice(0, 10)
      : [];

  const hasFilters = dateFilter || idFilter || showLspFilter;

  return (
    <Container className="mt--7" fluid>
      <Card className="bg-gradient-default shadow mb-4">
        <CardBody>
          <h6 className="text-uppercase text-light ls-1 mb-1">Salora Capital · NBFC</h6>
          <h2 className="text-white mb-0">{title}</h2>
        </CardBody>
      </Card>
      <Row>
        <Col xl="12">
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col xs="6">
                  <h3 className="mb-0">
                    {title}{" "}
                    {!loading && (
                      <Badge color="secondary" pill className="ml-2">
                        {rows.length}
                      </Badge>
                    )}
                  </h3>
                </Col>
                <Col xs="6" className="text-right">
                  <Button color="primary" size="sm" onClick={() => fetchData()} disabled={loading}>
                    {loading ? "Loading…" : "Refresh"}
                  </Button>
                </Col>
              </Row>

              {hasFilters && (
                <Form onSubmit={handleSearch} className="mt-3">
                  <div className="d-flex align-items-end filter-bar" style={{ gap: "10px" }}>
                    {idFilter && (
                      <>
                        <FormGroup className="mb-0">
                          <Label className="mb-1 small font-weight-bold">Search by</Label>
                          <Input type="select" bsSize="sm" value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}>
                            {idFilters.map((f) => (
                              <option key={f.param} value={f.param}>{f.label}</option>
                            ))}
                          </Input>
                        </FormGroup>
                        <FormGroup className="mb-0">
                          <Label className="mb-1 small font-weight-bold">Value</Label>
                          <Input bsSize="sm" placeholder="Enter id…" value={filterValue}
                            onChange={(e) => setFilterValue(e.target.value)} />
                        </FormGroup>
                      </>
                    )}
                    {showLspFilter && (
                      <FormGroup className="mb-0">
                        <Label className="mb-1 small font-weight-bold">LSP</Label>
                        <Input type="select" bsSize="sm" value={selectedLsp}
                          onChange={(e) => setSelectedLsp(e.target.value)} style={{ minWidth: "140px" }}>
                          <option value="">All LSPs</option>
                          {(Array.isArray(lspList) ? lspList : []).map((l) => (
                            <option key={l.id} value={l.id}>{l.name}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    )}
                    {statusFilter && (
                      <FormGroup className="mb-0">
                        <Label className="mb-1 small font-weight-bold">Status</Label>
                        <Input type="select" bsSize="sm" value={selectedStatus}
                          onChange={(e) => setSelectedStatus(e.target.value)} style={{ minWidth: "130px" }}>
                          <option value="">All Status</option>
                          {statusFilter.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    )}
                    {schemeFilter && (
                      <FormGroup className="mb-0">
                        <Label className="mb-1 small font-weight-bold">Scheme</Label>
                        <Input type="select" bsSize="sm" value={selectedScheme}
                          onChange={(e) => handleSchemeChange(e.target.value)} style={{ minWidth: "100px" }}>
                          <option value="fip">FIP</option>
                          <option value="pif">PIF</option>
                        </Input>
                      </FormGroup>
                    )}
                    {dateFilter && (
                      <>
                        <FormGroup className="mb-0">
                          <Label className="mb-1 small font-weight-bold">From Date</Label>
                          <Input type="date" bsSize="sm" value={fromDate} max={toISO(today)}
                            onChange={(e) => setFromDate(e.target.value)} />
                        </FormGroup>
                        <FormGroup className="mb-0">
                          <Label className="mb-1 small font-weight-bold">To Date</Label>
                          <Input type="date" bsSize="sm" value={toDate} max={toISO(today)}
                            onChange={(e) => setToDate(e.target.value)} />
                        </FormGroup>
                      </>
                    )}
                    <Button color="primary" size="sm" type="submit" disabled={loading}>Search</Button>
                    <Button color="secondary" size="sm" type="button" onClick={handleReset} disabled={loading}>Reset</Button>
                  </div>
                </Form>
              )}

              {summaryEndpoint && summary && (
                <Row className="mt-3 pt-3 border-top align-items-center">
                  <Col xs="6" md="3" className="mb-2 mb-md-0">
                    <div className="text-uppercase text-muted small font-weight-bold">Total Collection</div>
                    <div className="h3 mb-0 font-weight-bold" style={{ color: "#2e7d32" }}>{money(summary.totalCollection)}</div>
                  </Col>
                  <Col xs="6" md="3" className="mb-2 mb-md-0">
                    <div className="text-uppercase text-muted small font-weight-bold">Advance Collection</div>
                    <div className="h3 mb-0 font-weight-bold" style={{ color: "#11998e" }}>{money(summary.advanceCollection)}</div>
                  </Col>
                  <Col xs="6" md="3" className="mb-2 mb-md-0">
                    <div className="text-uppercase text-muted small font-weight-bold">Regular Collection</div>
                    <div className="h3 mb-0 font-weight-bold" style={{ color: "#1e4fbf" }}>{money(summary.regularCollection)}</div>
                  </Col>
                  <Col xs="6" md="3" className="mb-2 mb-md-0">
                    <div className="text-uppercase text-muted small font-weight-bold">Overdue Collection</div>
                    <div className="h3 mb-0 font-weight-bold" style={{ color: "#d9534f" }}>{money(summary.overdueCollection)}</div>
                  </Col>
                </Row>
              )}
            </CardHeader>

            {loading && (
              <CardBody className="text-center text-muted py-5">Loading {title.toLowerCase()}…</CardBody>
            )}
            {!loading && error && (
              <CardBody className="text-center text-danger py-5">{error}</CardBody>
            )}
            {!loading && !error && rows.length === 0 && (
              <CardBody className="text-center text-muted py-5">
                No {title.toLowerCase()} records found{dateFilter ? " for the selected filters." : "."}
              </CardBody>
            )}
            {!loading && !error && rows.length > 0 && (
              <div className="table-scroll">
              <Table className="align-items-center table-flush">
                <thead className="thead-light">
                  <tr>
                    {cols.map((c) => (
                      <th scope="col" key={c}>{labels[c] || humanize(c)}</th>
                    ))}
                    {viewLink && <th scope="col">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.id || row._id || i}>
                      {cols.map((c) => (
                        <td key={c}>{formatters[c] ? formatters[c](row[c], row) : renderCell(row[c])}</td>
                      ))}
                      {viewLink && (
                        <td>
                          {viewLink(row) ? (
                            <Link to={viewLink(row)} target="_blank">
                              <Button color="primary" size="sm" outline>View</Button>
                            </Link>
                          ) : "—"}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
            )}
            {!loading && !error && (rows.length > 0 || page > 0) && (
              <div className="d-flex justify-content-between align-items-center px-3 py-3">
                <Button color="secondary" size="sm" outline disabled={page === 0 || loading} onClick={() => goToPage(page - 1)}>
                  <i className="fa fa-chevron-left mr-1" /> Prev
                </Button>
                <span className="text-muted small">Page {page + 1}</span>
                <Button color="secondary" size="sm" outline disabled={rows.length < PAGE_SIZE || loading} onClick={() => goToPage(page + 1)}>
                  Next <i className="fa fa-chevron-right ml-1" />
                </Button>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EntityList;
